import { FC, useMemo } from 'react'

import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'

import Clickable from 'core/components/lib/Clickable'
import { Page } from 'core/components/lib/Page'
import PageHeader from 'core/components/lib/PageHeader'
import Pagination from 'core/components/lib/Pagination'
import Table from 'core/components/lib/Table'
import { error as errorToken } from 'core/styles/tokens'
import { ResourceCatalogEntry } from 'core/types/generated/resources'
import { StaticFieldSchema } from 'core/utils/generated/formSchemas'

import deriveColumns, { JsonRecord } from './deriveColumns'
import { redactSecrets } from './redactSecrets'
import {
  hasCreatePage,
  resourceCreatePath,
  resourceDetailPath,
} from './resourcePaths'
import type {
  ResourceListPaging,
  ResourcePagination,
} from './useResourceListPaging'
import useResourceNavigate from './useResourceNavigate'

/** What the generated list hooks return, so a caller can spread one. */
export type ResourceListResult = {
  data?: Array<JsonRecord>
  pagination?: ResourcePagination
  isLoading: boolean
  error: unknown
}

type ResourceListViewProps = ResourceListResult & {
  resource: ResourceCatalogEntry
  schema: Array<StaticFieldSchema>
  paging: ResourceListPaging
}

const ResourceListView: FC<ResourceListViewProps> = ({
  resource,
  schema,
  data,
  pagination,
  isLoading,
  error,
  paging,
}) => {
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useResourceNavigate()

  const { getPaginationProps, pagingModel, rangeStart } = paging

  const rows = useMemo(() => (data ?? []).map(redactSecrets), [data])

  if (error != null) {
    return (
      <Page variant='full'>
        <PageHeader title={resource.label.plural} />
        <ErrorMessage>
          {error instanceof Error ? error.message : 'Failed to load'}
        </ErrorMessage>
      </Page>
    )
  }

  return (
    <Page variant='full'>
      <PageHeader
        action={
          hasCreatePage(resource.key) && (
            <StyledCreateClickable
              to={resourceCreatePath(resource.key, { companyId })}
              variant='link'
            >
              {`Create ${resource.label.singular}`}
            </StyledCreateClickable>
          )
        }
        title={resource.label.plural}
      />
      <Table
        columns={deriveColumns(schema)}
        data={rows}
        emptyHeading={`No ${resource.label.plural.toLowerCase()} yet`}
        getRowId={(row) => String(row.id ?? '')}
        isLoading={isLoading}
        onRowClick={(row) =>
          navigate(
            resourceDetailPath(resource.key, {
              companyId,
              id: String(row.id),
            }),
          )
        }
      />
      {pagingModel !== 'none' && (
        <PaginationWrapper>
          <Pagination
            {...getPaginationProps(pagination)}
            isLoading={isLoading}
            label={
              pagination?.total != null && rows.length > 0
                ? `${rangeStart + 1}–${rangeStart + rows.length} of ${
                    pagination.total
                  }`
                : undefined
            }
          />
        </PaginationWrapper>
      )}
    </Page>
  )
}

export default ResourceListView

const ErrorMessage = styled.p`
  color: ${(p) => p.theme.alert ?? errorToken};
  font-size: 14px;
`

const PaginationWrapper = styled.div`
  margin-top: 12px;
`

const StyledCreateClickable = styled(Clickable)`
  margin-right: 16px;
`
